import { useCallback, useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Play, Filter, X } from "lucide-react";
import { toast } from "sonner";
import { useEntity } from "@/context/EntityContext";

const FIELD_OPTS = [
  { value: "description", label: "Description (note)" },
  { value: "amount", label: "Amount" },
  { value: "category", label: "Category" },
  { value: "account", label: "Account" },
  { value: "type", label: "Type" },
];
const TEXT_OPS = [
  { value: "contains", label: "contains" },
  { value: "equals", label: "equals" },
  { value: "starts_with", label: "starts with" },
  { value: "ends_with", label: "ends with" },
];
const NUM_OPS = [
  { value: "gt", label: ">" },
  { value: "gte", label: "≥" },
  { value: "lt", label: "<" },
  { value: "lte", label: "≤" },
  { value: "eq", label: "=" },
  { value: "between", label: "between" },
];

const ACTION_KINDS = [
  { value: "recategorize", label: "Set category" },
  { value: "move_entity", label: "Move to entity" },
  { value: "add_tag", label: "Add tag" },
];

// Stable client-side ID for form condition/action rows.
let _rid = 0;
function newRid() { _rid += 1; return `r${_rid}`; }
function withRid(row) { return { _rid: newRid(), ...row }; }

const emptyForm = () => ({
  name: "",
  enabled: true,
  conditions: [withRid({ field: "description", op: "contains", value: "", value_max: "" })],
  actions: [withRid({ kind: "recategorize", value: "" })],
});

export default function Rules() {
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState({ expense: [], income: [], saving: [] });
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [running, setRunning] = useState(false);
  const { entities } = useEntity();

  const load = useCallback(async () => {
    try {
      const [{ data: rs }, { data: cats }] = await Promise.all([
        api.get("/rules"),
        api.get("/categories"),
      ]);
      setRules(rs);
      setCategories(cats);
    } catch (err) {
      console.warn("[Rules] load failed:", err?.message);
      toast.error("Couldn't load rules");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name,
      enabled: r.enabled,
      conditions: (r.conditions || []).length
        ? r.conditions.map((c) => withRid({
            field: c.field,
            op: c.op,
            value: c.value ?? "",
            value_max: c.value_max == null ? "" : String(c.value_max),
          }))
        : [withRid({ field: "description", op: "contains", value: "", value_max: "" })],
      actions: (r.actions || []).length
        ? r.actions.map((a) => withRid({ kind: a.kind, value: a.value }))
        : [withRid({ kind: "recategorize", value: "" })],
    });
    setOpen(true);
  };

  const addCondition = () =>
    setForm((f) => ({ ...f, conditions: [...f.conditions, withRid({ field: "description", op: "contains", value: "", value_max: "" })] }));
  const removeCondition = (i) =>
    setForm((f) => ({ ...f, conditions: f.conditions.filter((_, idx) => idx !== i) }));
  const setCondition = (i, patch) =>
    setForm((f) => ({ ...f, conditions: f.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));

  const addAction = () =>
    setForm((f) => ({ ...f, actions: [...f.actions, withRid({ kind: "recategorize", value: "" })] }));
  const removeAction = (i) =>
    setForm((f) => ({ ...f, actions: f.actions.filter((_, idx) => idx !== i) }));
  const setAction = (i, patch) =>
    setForm((f) => ({ ...f, actions: f.actions.map((a, idx) => (idx === i ? { ...a, ...patch } : a)) }));

  const allCategories = [
    ...(categories.expense || []),
    ...(categories.income || []),
    ...(categories.saving || []),
  ];

  const save = async () => {
    if (!form.name.trim()) return toast.error("Rule name is required");
    if (form.actions.length === 0) return toast.error("Add at least one action");
    const payload = {
      name: form.name.trim(),
      enabled: form.enabled,
      conditions: form.conditions
        .filter((c) => c.field && c.op)
        .map((c) => {
          const out = { field: c.field, op: c.op };
          if (c.field === "amount") {
            out.value = c.value === "" ? null : String(c.value);
            if (c.op === "between") out.value_max = c.value_max === "" ? null : parseFloat(c.value_max);
          } else {
            out.value = c.value || "";
          }
          return out;
        }),
      actions: form.actions.filter((a) => a.kind && a.value).map((a) => ({ kind: a.kind, value: a.value })),
    };
    if (payload.actions.length === 0) return toast.error("Each action needs a value");
    try {
      if (editing) {
        await api.patch(`/rules/${editing.id}`, payload);
        toast.success("Rule updated");
      } else {
        await api.post("/rules", payload);
        toast.success("Rule created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/rules/${deleteId}`);
      toast.success("Rule deleted");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const toggle = async (r) => {
    try {
      await api.patch(`/rules/${r.id}`, { enabled: !r.enabled });
      load();
    } catch (err) {
      toast.error("Failed to toggle rule");
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/rules/run");
      toast.success(`Applied to ${data.applied} of ${data.scanned} transactions`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rules run failed");
    } finally {
      setRunning(false);
    }
  };

  const summarizeCondition = (c) => {
    const field = FIELD_OPTS.find((f) => f.value === c.field)?.label || c.field;
    const ops = c.field === "amount" ? NUM_OPS : TEXT_OPS;
    const op = ops.find((o) => o.value === c.op)?.label || c.op;
    const val = c.op === "between" ? `${c.value}–${c.value_max}` : c.value;
    return `${field} ${op} "${val}"`;
  };

  const summarizeAction = (a) => {
    if (a.kind === "recategorize") return `→ Category: ${a.value}`;
    if (a.kind === "move_entity") {
      const e = entities.find((x) => x.id === a.value);
      return `→ Move to entity: ${e?.name || a.value}`;
    }
    if (a.kind === "add_tag") return `→ Tag: ${a.value}`;
    return `→ ${a.kind}: ${a.value}`;
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Automation</div>
          <h1 className="font-display text-4xl mt-2">Rules</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1 max-w-xl">
            When bank transactions sync, rules auto-tag, recategorize, or move them by matching your saved filters.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="rules-run-now-btn"
            variant="outline"
            className="rounded-full h-11 px-4"
            onClick={runNow}
            disabled={running || rules.length === 0}
          >
            <Play className="w-4 h-4 mr-2" /> {running ? "Running…" : "Run rules now"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="rules-new-btn"
                className="rounded-md btn-brand h-11 px-5"
                onClick={openCreate}
              >
                <Plus className="w-4 h-4 mr-2" /> New rule
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editing ? "Edit rule" : "Create rule"}</DialogTitle>
                <DialogDescription>
                  Match new bank transactions with these conditions and apply the actions below.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-5">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Name</Label>
                  <Input
                    data-testid="rule-form-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Uber rides → Transport"
                    className="mt-1"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                      Conditions <span className="text-[var(--muted-text)] normal-case">(all must match)</span>
                    </Label>
                    <Button size="sm" variant="ghost" onClick={addCondition} data-testid="rule-add-condition">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {form.conditions.map((c, i) => {
                      const isAmt = c.field === "amount";
                      const ops = isAmt ? NUM_OPS : TEXT_OPS;
                      return (
                        <div key={c._rid} className="flex gap-2 items-start" data-testid={`rule-cond-${i}`}>
                          <Select value={c.field} onValueChange={(v) => setCondition(i, { field: v, op: v === "amount" ? "gt" : "contains" })}>
                            <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {FIELD_OPTS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Select value={c.op} onValueChange={(v) => setCondition(i, { op: v })}>
                            <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ops.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {c.field === "type" ? (
                            <Select value={c.value || ""} onValueChange={(v) => setCondition(i, { value: v })}>
                              <SelectTrigger className="flex-1 h-9"><SelectValue placeholder="Pick a type" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="expense">Expense</SelectItem>
                                <SelectItem value="income">Income</SelectItem>
                                <SelectItem value="saving">Saving</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              className="flex-1 h-9"
                              value={c.value}
                              onChange={(e) => setCondition(i, { value: e.target.value })}
                              placeholder={isAmt ? "0.00" : "value"}
                              type={isAmt ? "number" : "text"}
                            />
                          )}
                          {isAmt && c.op === "between" && (
                            <Input
                              className="w-24 h-9"
                              value={c.value_max}
                              onChange={(e) => setCondition(i, { value_max: e.target.value })}
                              placeholder="max"
                              type="number"
                            />
                          )}
                          <Button size="icon" variant="ghost" onClick={() => removeCondition(i)} disabled={form.conditions.length <= 1}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                      Actions <span className="text-[var(--muted-text)] normal-case">(all applied on match)</span>
                    </Label>
                    <Button size="sm" variant="ghost" onClick={addAction} data-testid="rule-add-action">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {form.actions.map((a, i) => (
                      <div key={a._rid} className="flex gap-2 items-start" data-testid={`rule-action-${i}`}>
                        <Select value={a.kind} onValueChange={(v) => setAction(i, { kind: v, value: "" })}>
                          <SelectTrigger className="w-44 h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ACTION_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {a.kind === "recategorize" ? (
                          <Select value={a.value} onValueChange={(v) => setAction(i, { value: v })}>
                            <SelectTrigger className="flex-1 h-9"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                            <SelectContent>
                              {["expense", "income", "saving"].map((t) => (
                                (categories[t] || []).length > 0 && (
                                  <SelectGroup key={t}>
                                    <SelectLabel className="capitalize">{t}</SelectLabel>
                                    {(categories[t] || []).map((c) => (
                                      <SelectItem key={`${t}-${c.id}`} value={c.name}>
                                        <span className="inline-flex items-center gap-2">
                                          <span style={{ color: c.color }}>{c.icon}</span>{c.name}
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                )
                              ))}
                            </SelectContent>
                          </Select>
                        ) : a.kind === "move_entity" ? (
                          <Select value={a.value} onValueChange={(v) => setAction(i, { value: v })}>
                            <SelectTrigger className="flex-1 h-9"><SelectValue placeholder="Pick an entity" /></SelectTrigger>
                            <SelectContent>
                              {entities.map((e) => (
                                <SelectItem key={e.id} value={e.id}>
                                  <span className="inline-flex items-center gap-2">
                                    <span style={{ color: e.color }}>{e.icon}</span>{e.name}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            className="flex-1 h-9"
                            value={a.value}
                            onChange={(e) => setAction(i, { value: e.target.value })}
                            placeholder="tag value"
                          />
                        )}
                        <Button size="icon" variant="ghost" onClick={() => removeAction(i)} disabled={form.actions.length <= 1}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="rule-form-enabled" />
                  <span className="text-sm text-[var(--muted-text)]">Enabled</span>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  data-testid="rule-form-save"
                  onClick={save}
                  className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
                >
                  {editing ? "Save changes" : "Create rule"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        {rules.length === 0 ? (
          <div data-testid="rules-empty" className="p-10 text-center text-sm text-[var(--muted-text)]">
            <Filter className="w-8 h-8 mx-auto mb-3 opacity-40" />
            No rules yet. Create one to auto-categorize, move, or tag newly-synced bank transactions.
          </div>
        ) : (
          <div className="divide-y divide-[var(--divider)]">
            {rules.map((r) => (
              <div key={r.id} data-testid={`rule-row-${r.id}`} className="flex items-center gap-3 px-6 py-4 hover:bg-[var(--row-hover)]">
                <Switch
                  checked={r.enabled}
                  onCheckedChange={() => toggle(r)}
                  data-testid={`rule-toggle-${r.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{r.name}</div>
                  <div className="text-xs text-[var(--muted-text)] mt-0.5 truncate">
                    <span className="mr-2">If</span>
                    {(r.conditions || []).length === 0
                      ? <em>every transaction</em>
                      : r.conditions.map(summarizeCondition).join(" · AND ")}
                  </div>
                  <div className="text-xs text-[var(--muted-text)] truncate">
                    {(r.actions || []).map(summarizeAction).join("  ·  ")}
                  </div>
                </div>
                <div className="text-xs text-[var(--muted-text)] hidden md:block whitespace-nowrap">
                  {r.runs || 0} match{r.runs === 1 ? "" : "es"}
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`rule-edit-${r.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setDeleteId(r.id)} data-testid={`rule-delete-${r.id}`}>
                    <Trash2 className="w-4 h-4 text-[#E63946]" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete rule?</AlertDialogTitle>
            <AlertDialogDescription>
              This rule will no longer run against future syncs. Existing transactions are untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="rule-delete-confirm" onClick={remove} className="bg-[#E63946] hover:bg-[#c92e3b] text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
